import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import api from '../services/api';

export const useCollaboration = (caseId) => {
  const { currentUser } = useAuth();
  const [websocket, setWebsocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [activeUsers, setActiveUsers] = useState([]);
  const [activeEditors, setActiveEditors] = useState({});
  const [connectionError, setConnectionError] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 3; // Reduce max attempts
  const connectionInProgressRef = useRef(false);
  const batecRef = useRef(null);
  const toastTimestampsRef = useRef({}); // To prevent duplicate toasts

  const showToastWithDebounce = useCallback((key, message, options) => {
    const now = Date.now();
    const lastShown = toastTimestampsRef.current[key] || 0;
    
    // Only show toast if it's been more than 5 seconds since the last one
    if (now - lastShown > 5000) {
      toast.info(message, options);
      toastTimestampsRef.current[key] = now;
    }
  }, []);

  const handleMessage = useCallback((message) => {
    const { type } = message;

    switch (type) {
      case 'user_joined':
        setActiveUsers(prev => {
          // Remove any existing instances of this user first
          const filteredUsers = prev.filter(u => u.user_id !== message.user.user_id);
          const newUsers = [...filteredUsers, message.user];
          return newUsers;
        });
        // Only show toast for other users, not for the current user
        if (message.user.user_id !== currentUser?.id) {
          showToastWithDebounce(
            `join_${message.user.user_id}`,
            `${message.user.full_name} joined the collaboration`,
            { position: 'bottom-right', autoClose: 2000 }
          );
        }
        break;

      case 'user_left':
        setActiveUsers(prev => prev.filter(u => u.user_id !== message.user.user_id));
        // Only show toast for other users, not for the current user
        if (message.user.user_id !== currentUser?.id) {
          showToastWithDebounce(
            `leave_${message.user.user_id}`,
            `${message.user.full_name} left the collaboration`,
            { position: 'bottom-right', autoClose: 2000 }
          );
        }
        break;

      case 'active_users':
        // Deduplicate users by user_id, keeping the one with the latest last_seen
        const userMap = new Map();
        (message.users || []).forEach(user => {
          const existing = userMap.get(user.user_id);
          if (!existing || !user.last_seen || 
              (existing.last_seen && user.last_seen > existing.last_seen)) {
            userMap.set(user.user_id, user);
          }
        });
        const uniqueUsers = Array.from(userMap.values());
        setActiveUsers(uniqueUsers);
        break;

      case 'active_editors':
        setActiveEditors(message.editors);
        break;

      case 'editing_started':
        setActiveEditors(prev => ({
          ...prev,
          [message.section]: {
            user_id: message.user.user_id,
            username: message.user.username,
            full_name: message.user.full_name,
            started_at: message.timestamp
          }
        }));
        break;

      case 'editing_stopped':
        setActiveEditors(prev => {
          const newEditors = { ...prev };
          delete newEditors[message.section];
          return newEditors;
        });
        break;

      case 'edit_conflict':
        toast.warning(
          `${message.current_editor.full_name} is currently editing this section`,
          { autoClose: 5000 }
        );
        break;

      case 'section_force_unlocked':
        toast.info(`Section "${message.section}" was unlocked by ${message.admin.full_name}`);
        setActiveEditors(prev => {
          const newEditors = { ...prev };
          delete newEditors[message.section];
          return newEditors;
        });
        break;

      // Canvi d'una entitat concreta, emès pel servidor amb l'objecte sencer.
      // A diferència de 'content_change', que només deia «alguna cosa ha
      // canviat» i obligava a recarregar la secció, això es pot aplicar tal
      // qual a l'estat local: això és el que fa que l'edició sigui simultània
      // de debò i no una recàrrega amb avís.
      case 'entity_change':
        window.dispatchEvent(new CustomEvent('collaboration-entity-change', {
          detail: message
        }));
        break;

      case 'content_change':
        // This will be handled by individual components
        // Emit custom event that components can listen to
        window.dispatchEvent(new CustomEvent('collaboration-content-change', {
          detail: message
        }));
        break;

      case 'cursor_position':
        // Emit custom event for cursor position updates
        window.dispatchEvent(new CustomEvent('collaboration-cursor-position', {
          detail: message
        }));
        break;

      case 'pong':
        // Connection is alive
        break;

      case 'user_presence_update':
        // Update active users with presence information
        setActiveUsers(prev => {
          return prev.map(user => 
            user.user_id === message.user.user_id 
              ? { ...user, current_section: message.section, last_seen: message.timestamp }
              : user
          );
        });
        break;

      case 'chat_message_received':
        // Qui l'ha enviat ja el té a la pantalla (l'afegeix en rebre la resposta del
        // POST): el servidor no exclou la seva pròpia connexió en retransmetre, i sense
        // aquest filtre el missatge sortia duplicat per a qui l'escrivia. Mateix patró
        // que `user_joined`/`user_left` més amunt.
        if (message.message.user_id === currentUser?.id) break;

        // Handle received chat message
        setChatMessages(prev => {
          const exists = prev.some(msg => msg.id === message.message.id);
          if (exists) return prev;
          return [...prev, message.message];
        });
        // Emit custom event that Chat component can listen to
        window.dispatchEvent(new CustomEvent('collaboration-chat-message', {
          detail: message.message
        }));
        break;

      case 'error':
        console.error('Collaboration error:', message.message);
        toast.error(`Collaboration error: ${message.message}`);
        break;

      default:
    }
  }, [currentUser, showToastWithDebounce]);

  const connect = useCallback(async () => {
    if (!currentUser || !caseId) {
      setConnectionError(null);
      return;
    }

    // Prevent multiple concurrent connections
    if (connectionInProgressRef.current || (websocket && (websocket.readyState === WebSocket.CONNECTING || websocket.readyState === WebSocket.OPEN))) {
      return;
    }

    connectionInProgressRef.current = true;

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setConnectionError('No authentication token found');
        return;
      }

      // L'amfitrió del WebSocket ve de REACT_APP_WS_URL, que es congela en el
      // moment del build. Estava clavat a ws://localhost:8000, cosa que en
      // producció fallava dues vegades: amfitrió equivocat i, sobretot, `ws://`
      // des d'una pàgina servida per https, que el navegador bloqueja com a
      // contingut mixt sense dir gaire res.
      const wsBase = process.env.REACT_APP_WS_URL || 'ws://localhost:8000';

      // El token NO va a la URL.
      //
      // El navegador no deixa posar capçaleres en obrir un WebSocket, i abans
      // per això s'hi enganxava el token: `?token=eyJ…`. Però la URL se serveix
      // al registre, i el 09/08/2026 se'n van comptar 20 al del backend i 45 al
      // d'accés de Traefik, en clar i vàlids vuit hores.
      //
      // Ara es demana un tiquet amb el token a la capçalera —que no es
      // registra— i el WebSocket s'obre amb el tiquet: dura 30 segons i es crema
      // al primer ús, o sigui que al registre ja no hi ha res aprofitable.
      const { data } = await api.post(`/api/collaboration/case/${caseId}/ticket`);
      const wsUrl = `${wsBase}/api/collaboration/case/${caseId}?ticket=${encodeURIComponent(data.ticket)}`;
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        setIsConnected(true);
        setConnectionError(null);
        reconnectAttemptsRef.current = 0;
        connectionInProgressRef.current = false;
        
        // Batec.
        //
        // Abans només es pingava un cop en connectar-se, i el servidor no tenia
        // manera de saber si una connexió encara era viva: una pestanya tancada
        // de cop deixava l'usuari a la llista per sempre. Ara s'envia cada 25
        // segons i el servidor dona de baixa el que no diu res en 75.
        ws.send(JSON.stringify({ type: 'ping' }));
        clearInterval(batecRef.current);
        batecRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 25000);
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          handleMessage(message);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.onclose = (event) => {
        setIsConnected(false);
        setWebsocket(null);
        connectionInProgressRef.current = false;
        clearInterval(batecRef.current);
        
        // Only attempt to reconnect for unexpected closures and if we haven't exceeded max attempts
        if (event.code !== 1000 && reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current += 1;
          const delay = Math.min(2000 * reconnectAttemptsRef.current, 10000); // Longer delays
          
          reconnectTimeoutRef.current = setTimeout(() => {
            if (currentUser && caseId) { // Double check we still need to connect
              connect();
            }
          }, delay);
        } else if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
          setConnectionError('Failed to connect after multiple attempts');
          console.warn('Max reconnection attempts reached');
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setConnectionError('WebSocket connection error');
        connectionInProgressRef.current = false;
      };

      setWebsocket(ws);
    } catch (error) {
      console.error('Error creating WebSocket connection:', error);
      setConnectionError('Failed to create WebSocket connection');
      connectionInProgressRef.current = false;
    }
  }, [currentUser, caseId]); // eslint-disable-line react-hooks/exhaustive-deps

  const disconnect = useCallback(() => {
    clearInterval(batecRef.current);
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    if (websocket && websocket.readyState !== WebSocket.CLOSED) {
      try {
        websocket.close(1000, 'User disconnecting');
      } catch (error) {
        console.warn('Error closing WebSocket:', error);
      }
    }
    
    setWebsocket(null);
    setIsConnected(false);
    setActiveUsers([]);
    setActiveEditors({});
    setConnectionError(null);
    // setChatMessages([]);
    reconnectAttemptsRef.current = 0;
    connectionInProgressRef.current = false;
  }, [websocket]);

  const sendMessage = useCallback((message) => {
    if (websocket && websocket.readyState === WebSocket.OPEN) {
      try {
        websocket.send(JSON.stringify(message));
      } catch (error) {
        console.error('Error sending WebSocket message:', error);
      }
    } else {
      console.warn('WebSocket is not ready to send messages. State:', websocket?.readyState);
    }
  }, [websocket]);

  const startEditing = useCallback((section) => {
    sendMessage({
      type: 'start_editing',
      section
    });
  }, [sendMessage]);

  const stopEditing = useCallback((section) => {
    sendMessage({
      type: 'stop_editing',
      section
    });
  }, [sendMessage]);

  const sendContentChange = useCallback((section, content) => {
    sendMessage({
      type: 'content_change',
      section,
      content
    });
  }, [sendMessage]);

  const sendCursorPosition = useCallback((section, position) => {
    sendMessage({
      type: 'cursor_position',
      section,
      position
    });
  }, [sendMessage]);

  const reportPresence = useCallback((section) => {
    sendMessage({
      type: 'user_presence',
      section
    });
  }, [sendMessage]);

  const sendChatMessage = useCallback((messageData) => {
    sendMessage({
      type: 'chat_message',
      ...messageData
    });
  }, [sendMessage]);

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    if (currentUser && caseId) {
      // Add a small delay to prevent rapid connection attempts
      const timer = setTimeout(() => {
        connect();
      }, 100);
      
      return () => {
        clearTimeout(timer);
        disconnect();
      };
    }
    return () => {
      disconnect();
    };
  }, [currentUser, caseId]); // eslint-disable-line react-hooks/exhaustive-deps


  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, []);

  return {
    isConnected,
    activeUsers,
    activeEditors,
    connectionError,
    chatMessages,
    connect,
    disconnect,
    startEditing,
    stopEditing,
    sendContentChange,
    sendCursorPosition,
    reportPresence,
    sendMessage,
    sendChatMessage
  };
};

export default useCollaboration;